import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Alert,
  Stack,
  useTheme,
  LinearProgress
} from '@mui/material';
import {
  Scale as ScaleIcon,
  Height as HeightIcon,
  FitnessCenter as FitnessCenterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as StableIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../utils/api';

const PhysicalMeasurementList = ({ onEdit, refreshTrigger }) => {
  const theme = useTheme();
  
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);
  const [weightTrend, setWeightTrend] = useState([]);

  useEffect(() => {
    fetchMeasurements();
    fetchWeightTrend();
  }, [refreshTrigger]);

  const fetchMeasurements = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/physical-measurements');
      setMeasurements(response.data.data || []);
    } catch (error) {
      setError('Failed to load measurements');
      console.error('Error fetching measurements:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeightTrend = async () => {
    try {
      const response = await api.get('/physical-measurements/trends/weight?days=90');
      const trends = response.data.data || [];
      
      const formattedTrends = trends.map(item => ({
        date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weight: item.weight
      }));
      
      setWeightTrend(formattedTrends);
    } catch (error) {
      console.error('Error fetching weight trends:', error);
    }
  };

  const handleMenuOpen = (event, measurement) => {
    setAnchorEl(event.currentTarget);
    setSelectedMeasurement(measurement);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMeasurement(null);
  };

  const handleEdit = () => {
    if (selectedMeasurement && onEdit) {
      onEdit(selectedMeasurement);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedMeasurement) return;

    try {
      await api.delete(`/physical-measurements/${selectedMeasurement._id}`);
      setMeasurements(prev => prev.filter(m => m._id !== selectedMeasurement._id));
      handleMenuClose();
    } catch (error) {
      console.error('Error deleting measurement:', error);
      setError('Failed to delete measurement');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getBmiCategory = (bmi) => {
    if (!bmi) return { category: 'N/A', color: 'default' };
    if (bmi < 18.5) return { category: 'Underweight', color: 'info' };
    if (bmi < 25) return { category: 'Normal', color: 'success' };
    if (bmi < 30) return { category: 'Overweight', color: 'warning' };
    return { category: 'Obese', color: 'error' };
  };

  const calculateBmi = (weight, height) => {
    if (!weight || !height) return null;
    const heightM = height / 100;
    return Math.round((weight / (heightM * heightM)) * 10) / 10;
  };

  const getProgressChange = (current, previous) => {
    if (!current || !previous) return null;
    const change = current - previous;
    const percentChange = ((change / previous) * 100).toFixed(1);
    
    return {
      value: change,
      percent: percentChange,
      trend: change > 0 ? 'up' : change < 0 ? 'down' : 'stable'
    };
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUpIcon sx={{ fontSize: 16 }} />;
      case 'down': return <TrendingDownIcon sx={{ fontSize: 16 }} />;
      default: return <StableIcon sx={{ fontSize: 16 }} />;
    }
  };

  const getTrendColor = (trend, metric) => {
    if (trend === 'stable') return 'text.secondary';
    
    // For weight and body fat, down is good (green), up is warning (orange)
    if (metric === 'weight' || metric === 'bodyFat') {
      return trend === 'down' ? 'success.main' : 'warning.main';
    }
    
    // For muscle mass, up is good (green), down is warning (orange)
    if (metric === 'muscle') {
      return trend === 'up' ? 'success.main' : 'warning.main';
    }
    
    return 'text.secondary';
  };

  if (loading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map(i => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={i}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Skeleton width="60%" height={32} />
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
                <Skeleton width="100%" height={20} sx={{ mb: 1 }} />
                <Skeleton width="80%" height={20} sx={{ mb: 1 }} />
                <Skeleton width="90%" height={20} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" width="100%" height={100} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (measurements.length === 0) {
    return (
      <Card sx={{ textAlign: 'center', py: 6 }}>
        <CardContent>
          <ScaleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No measurements recorded yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start tracking your physical progress with your first measurement!
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Get previous measurement for comparison
  const getComparison = (currentIndex) => {
    if (currentIndex < measurements.length - 1) {
      return measurements[currentIndex + 1];
    }
    return null;
  };

  return (
    <>
      {/* Weight Trend Chart */}
      {weightTrend.length > 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Weight Progress (Last 90 Days)
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={weightTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="weight" 
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={{ fill: theme.palette.primary.main }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {measurements.map((measurement, index) => {
          const bmi = calculateBmi(measurement.weight, measurement.height);
          const bmiCategory = getBmiCategory(bmi);
          const previousMeasurement = getComparison(index);
          
          const weightChange = getProgressChange(measurement.weight, previousMeasurement?.weight);
          const bodyFatChange = getProgressChange(measurement.bodyFatPercentage, previousMeasurement?.bodyFatPercentage);
          const muscleChange = getProgressChange(measurement.muscleMass, previousMeasurement?.muscleMass);

          return (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={measurement._id}>
              <Card 
                sx={{ 
                  height: '100%',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <CardContent>
                  {/* Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        📊 Measurement #{measurements.length - index}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(measurement.date)}
                      </Typography>
                    </Box>
                    <IconButton 
                      size="small"
                      onClick={(e) => handleMenuOpen(e, measurement)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {/* Key Metrics */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {measurement.weight && (
                      <Grid size={6}>
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                            <ScaleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="body2" fontWeight={600}>
                              {measurement.weight} kg
                            </Typography>
                            {weightChange && (
                              <Box 
                                display="flex" 
                                alignItems="center" 
                                color={getTrendColor(weightChange.trend, 'weight')}
                              >
                                {getTrendIcon(weightChange.trend)}
                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                  {weightChange.value > 0 ? '+' : ''}{weightChange.value.toFixed(1)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Weight
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {measurement.height && (
                      <Grid size={6}>
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                            <HeightIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                            <Typography variant="body2" fontWeight={600}>
                              {measurement.height} cm
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Height
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {measurement.bodyFatPercentage && (
                      <Grid size={6}>
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                            <Typography variant="body2" fontWeight={600}>
                              {measurement.bodyFatPercentage}%
                            </Typography>
                            {bodyFatChange && (
                              <Box 
                                display="flex" 
                                alignItems="center" 
                                color={getTrendColor(bodyFatChange.trend, 'bodyFat')}
                              >
                                {getTrendIcon(bodyFatChange.trend)}
                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                  {bodyFatChange.value > 0 ? '+' : ''}{bodyFatChange.value.toFixed(1)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Body Fat
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {measurement.muscleMass && (
                      <Grid size={6}>
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                            <FitnessCenterIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="body2" fontWeight={600}>
                              {measurement.muscleMass} kg
                            </Typography>
                            {muscleChange && (
                              <Box 
                                display="flex" 
                                alignItems="center" 
                                color={getTrendColor(muscleChange.trend, 'muscle')}
                              >
                                {getTrendIcon(muscleChange.trend)}
                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                  {muscleChange.value > 0 ? '+' : ''}{muscleChange.value.toFixed(1)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Muscle Mass
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* BMI */}
                  {bmi && (
                    <Box sx={{ mb: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" fontWeight={600}>
                          BMI: {bmi}
                        </Typography>
                        <Chip 
                          label={bmiCategory.category}
                          color={bmiCategory.color}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min((bmi / 35) * 100, 100)}
                        color={bmiCategory.color}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  )}

                  {/* Body Measurements */}
                  {Object.entries(measurement.measurements || {}).some(([_, value]) => value) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Body Measurements (cm)
                      </Typography>
                      <Grid container spacing={1}>
                        {Object.entries(measurement.measurements).map(([key, value]) => 
                          value ? (
                            <Grid size={6} key={key}>
                              <Typography variant="caption" color="text.secondary">
                                {key.charAt(0).toUpperCase() + key.slice(1)}: {value}
                              </Typography>
                            </Grid>
                          ) : null
                        )}
                      </Grid>
                    </Box>
                  )}

                  {/* Health Metrics */}
                  {(measurement.restingHeartRate || measurement.bloodPressure?.systolic) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Health Metrics
                      </Typography>
                      <Stack spacing={0.5}>
                        {measurement.restingHeartRate && (
                          <Typography variant="caption" color="text.secondary">
                            💓 Resting HR: {measurement.restingHeartRate} bpm
                          </Typography>
                        )}
                        {measurement.bloodPressure?.systolic && (
                          <Typography variant="caption" color="text.secondary">
                            🩺 BP: {measurement.bloodPressure.systolic}/{measurement.bloodPressure.diastolic} mmHg
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  )}

                  {/* Method and Privacy */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip 
                      label={measurement.measurementMethod}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                    {measurement.isPublic && (
                      <Chip 
                        label="Shared with trainers"
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {/* Notes */}
                  {measurement.notes && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        "{measurement.notes}"
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export default PhysicalMeasurementList;